import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { MessageSquare, Phone, PlayCircle, Video, MapPin } from 'lucide-react';
import { useInterviews } from '../../../hooks/queries/useInterviews';
import { useUpdateApplicationStatus } from '../../../hooks/mutations/useApplicationMutations';
import { useUpdateInterview } from '../../../hooks/mutations/useInterviewMutations';
import type { Interview } from '@applicant-reviewer/api';

type TabView = 'upcoming' | 'feedback';

function getAvatarColor(name: string): string {
  const colors = ['bg-blue-500 text-white', 'bg-green-500 text-white', 'bg-purple-500 text-white', 'bg-orange-500 text-white', 'bg-pink-500 text-white', 'bg-teal-500 text-white'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export default function ManagerInterviews() {
  const [activeTab, setActiveTab] = useState<TabView>('upcoming');
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);

  const { data: interviewData, isLoading, isError } = useInterviews();
  const updateStatus = useUpdateApplicationStatus();
  const updateInterview = useUpdateInterview();

  const upcomingCount = interviewData?.upcoming.length ?? 0;
  const feedbackCount = interviewData?.pastPendingFeedback.length ?? 0;

  const handleQuickAction = (action: string, interview: Interview) => {
    const interviewId = interview.interviewId;
    setSelectedInterview(null);

    if (action === 'View Profile') {
      window.location.href = `/interview/${interviewId}`;
      return;
    }

    if (action === 'Reschedule') {
      // Prompt for a new date/time and update the interview
      const newSlot = window.prompt('Enter new scheduled time (ISO format, e.g. 2026-03-15T10:00:00Z):');
      if (!newSlot) return;
      updateInterview.mutate({
        interviewId,
        payload: { availableSlots: [newSlot] },
      });
      return;
    }

    if (action === 'Cancel/No-Show') {
      setCancelConfirmId(interviewId);
    }
  };

  const handleConfirmCancel = (interviewId: string) => {
    updateInterview.mutate(
      { interviewId, payload: { status: 'canceled' } },
      { onSuccess: () => setCancelConfirmId(null) }
    );
  };

  const handleFeedback = (interview: Interview, decision: 'hire' | 'bench' | 'reject') => {
    if (!interview.applicationId) return;
    const statusMap = { hire: 'offered', bench: 'benched', reject: 'rejected' } as const;
    updateStatus.mutate({
      applicationId: interview.applicationId,
      payload: { status: statusMap[decision] },
    });
  };

  const handleStartInterview = (interview: Interview) => {
    window.location.href = `/interview/${interview.interviewId}`;
  };

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  const handleMessage = (phone: string) => {
    window.location.href = `sms:${phone}`;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatTime = (interview: Interview) =>
    interview.scheduledAt ? format(parseISO(interview.scheduledAt), 'h:mm a') : '';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-sm text-gray-400">Loading interviews…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-sm text-red-500">Failed to load interviews. Please try again.</p>
      </div>
    );
  }

  const upcoming = interviewData?.upcoming ?? [];
  const pastPendingFeedback = interviewData?.pastPendingFeedback ?? [];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="px-6 pt-6 pb-4 border-b border-gray-100">
        <h1 className="text-2xl font-extrabold text-gray-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Interviews
        </h1>
      </header>

      {/* Segmented Control Tabs */}
      <div className="px-6 py-4">
        <div className="bg-gray-100 rounded-xl p-1 flex">
          <button
            type="button"
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'upcoming'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Upcoming {upcomingCount > 0 && `(${upcomingCount})`}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('feedback')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'feedback'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Needs Feedback {feedbackCount > 0 && `(${feedbackCount})`}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-6 pb-6">
        {activeTab === 'upcoming' && (
          <div className="space-y-6">
            {/* Up Next Card (First Interview) */}
            {upcoming.length > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">Up Next</span>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                    Starts in 15 mins
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-16 h-16 rounded-full ${getAvatarColor(upcoming[0].candidateName)} flex items-center justify-center text-xl font-bold`}>
                    {getInitials(upcoming[0].candidateName)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{upcoming[0].candidateName}</h3>
                    <p className="text-sm text-gray-600">{upcoming[0].role}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        {upcoming[0].type === 'virtual' ? (
                          <><Video className="w-3 h-3" /> Virtual</>
                        ) : (
                          <><MapPin className="w-3 h-3" /> In-Person</>
                        )}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{formatTime(upcoming[0])}</span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled
                    className="flex-1 h-10 rounded-xl border-2 border-gray-200 bg-white text-gray-400 text-sm font-semibold flex items-center justify-center gap-2 cursor-not-allowed"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Message
                  </button>
                  <button
                    type="button"
                    disabled
                    className="flex-1 h-10 rounded-xl border-2 border-gray-200 bg-white text-gray-400 text-sm font-semibold flex items-center justify-center gap-2 cursor-not-allowed"
                  >
                    <Phone className="w-4 h-4" />
                    Call
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStartInterview(upcoming[0])}
                    className="flex-1 h-10 rounded-xl bg-[#0156FF] text-white text-sm font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <PlayCircle className="w-4 h-4 fill-current" />
                    Start
                  </button>
                </div>
              </div>
            )}

            {/* Timeline List (Rest of Interviews) */}
            {upcoming.length > 1 && (
              <div className="space-y-3">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Later Today</h2>
                {upcoming.slice(1).map((interview) => (
                  <button
                    key={interview.interviewId}
                    type="button"
                    onClick={() => setSelectedInterview(interview)}
                    className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all"
                  >
                    <div className="w-16 text-center">
                      <p className="text-lg font-bold text-gray-900">{formatTime(interview)}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-full ${getAvatarColor(interview.candidateName)} flex items-center justify-center text-base font-bold flex-shrink-0`}>
                      {getInitials(interview.candidateName)}
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-base font-semibold text-gray-900">{interview.candidateName}</h3>
                      <p className="text-sm text-gray-500">{interview.role}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {interview.type === 'virtual' ? (
                        <Video className="w-4 h-4 text-gray-400" />
                      ) : (
                        <MapPin className="w-4 h-4 text-gray-400" />
                      )}
                      {interview.status === 'pending_confirmation' && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-semibold">
                          Pending
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {upcoming.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No upcoming interviews scheduled.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="space-y-4">
            {pastPendingFeedback.map((interview) => (
              <div key={interview.interviewId} className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-full ${getAvatarColor(interview.candidateName)} flex items-center justify-center text-lg font-bold`}>
                    {getInitials(interview.candidateName)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      How was your interview with {interview.candidateName}?
                    </h3>
                    <p className="text-sm text-gray-500">{interview.role} • {formatTime(interview)}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleFeedback(interview, 'hire')}
                    disabled={updateStatus.isPending}
                    className="flex-1 h-11 rounded-xl border-2 border-green-500 text-green-700 text-sm font-bold hover:bg-green-50 transition-colors disabled:opacity-50"
                  >
                    Hire
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFeedback(interview, 'bench')}
                    disabled={updateStatus.isPending}
                    className="flex-1 h-11 rounded-xl border-2 border-yellow-500 text-yellow-700 text-sm font-bold hover:bg-yellow-50 transition-colors disabled:opacity-50"
                  >
                    Bench
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFeedback(interview, 'reject')}
                    disabled={updateStatus.isPending}
                    className="flex-1 h-11 rounded-xl border-2 border-red-500 text-red-700 text-sm font-bold hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}

            {pastPendingFeedback.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">All interviews have feedback!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Action Sheet Modal */}
      {selectedInterview && (
        <div className="fixed inset-0 z-50 flex items-end">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setSelectedInterview(null)}
            aria-label="Close action sheet"
          />
          <div className="relative w-full rounded-t-3xl bg-white px-6 pb-6 pt-4 shadow-2xl">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-200 mb-4" />

            <div className="flex items-center gap-3 mb-5">
              <div className={`w-12 h-12 rounded-full ${getAvatarColor(selectedInterview.candidateName)} flex items-center justify-center text-base font-bold`}>
                {getInitials(selectedInterview.candidateName)}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedInterview.candidateName}</h3>
                <p className="text-sm text-gray-500">{selectedInterview.role} • {formatTime(selectedInterview)}</p>
              </div>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handleQuickAction('View Profile', selectedInterview)}
                className="w-full h-12 rounded-xl border border-gray-200 text-gray-700 text-base font-semibold hover:bg-gray-50 transition-colors"
              >
                View Profile
              </button>
              <button
                type="button"
                onClick={() => handleQuickAction('Reschedule', selectedInterview)}
                className="w-full h-12 rounded-xl border border-gray-200 text-gray-700 text-base font-semibold hover:bg-gray-50 transition-colors"
              >
                Reschedule
              </button>
              <button
                type="button"
                onClick={() => handleQuickAction('Cancel/No-Show', selectedInterview)}
                className="w-full h-12 rounded-xl border border-red-200 text-red-600 text-base font-semibold hover:bg-red-50 transition-colors"
              >
                Cancel / No-Show
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel/No-Show Confirmation Dialog */}
      {cancelConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setCancelConfirmId(null)}
            aria-label="Dismiss cancel confirmation"
          />
          <div className="relative w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Interview?</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will mark the interview as canceled. The candidate will not be automatically notified.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setCancelConfirmId(null)}
                className="flex-1 h-11 rounded-xl border border-gray-300 text-gray-600 font-semibold"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={() => handleConfirmCancel(cancelConfirmId)}
                disabled={updateInterview.isPending}
                className="flex-1 h-11 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50"
              >
                {updateInterview.isPending ? 'Canceling…' : 'Cancel Interview'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
